import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Container,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import BarChartIcon from '@mui/icons-material/BarChart';
import DownloadIcon from '@mui/icons-material/Download';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SpeedIcon from '@mui/icons-material/Speed';
import PublicIcon from '@mui/icons-material/Public';
import { api } from '../utils/api';
import { LineChart, Line, BarChart as RechartsBarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

function MasterAdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  // Shuls state
  const [shuls, setShuls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [shulToDelete, setShulToDelete] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Registrations state
  const [registrations, setRegistrations] = useState([]);
  const [registrationsLoading, setRegistrationsLoading] = useState(false);
  const [registrationFilter, setRegistrationFilter] = useState('pending');
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [excludeBots, setExcludeBots] = useState(true);
  const [selectedShulFilter, setSelectedShulFilter] = useState('');
  const [selectedTrafficSource, setSelectedTrafficSource] = useState('');
  const [selectedDeviceType, setSelectedDeviceType] = useState('');

  useEffect(() => {
    fetchShuls();
    fetchRegistrations();
  }, []);

  const fetchShuls = async () => {
    try {
      const data = await api.get('/master-admin/shuls/');
      setShuls(data);
      setError('');
    } catch (err) {
      if (err.message.includes('403')) {
        setError('Access Denied: You must be a master admin to view this page.');
      } else {
        setError(`Failed to load shuls: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditGlobalMemorial = () => {
    navigate('/master-admin/memorial-settings');
  };

  const handleViewDisplay = (slug) => {
    window.open(`/display/${slug}`, '_blank');
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchShuls();
  };

  const handleDeleteClick = (shul) => {
    setShulToDelete(shul);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setShulToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!shulToDelete) return;

    try {
      await api.delete(`/master-admin/shuls/${shulToDelete.id}/delete/`);

      // Remove the deleted shul from the list
      setShuls(shuls.filter(s => s.id !== shulToDelete.id));

      setSuccessMessage(`Successfully deleted "${shulToDelete.name}" and associated user account.`);
      setError('');

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError(`Failed to delete shul: ${err.message}`);
    } finally {
      setDeleteDialogOpen(false);
      setShulToDelete(null);
    }
  };

  const handleLoginAsUser = async (shul) => {
    try {
      const response = await api.post(`/master-admin/shuls/${shul.id}/login-as/`);

      // Clear all existing auth data
      localStorage.clear();

      // Set the new token (use 'authToken' to match the app's convention)
      localStorage.setItem('authToken', response.token);

      // Store user data (without is_staff so Header doesn't show Master Admin button)
      const userData = {
        id: response.user.id,
        email: response.user.email,
        username: response.user.username,
        is_staff: false  // Important: regular user, not master admin
      };
      localStorage.setItem('userData', JSON.stringify(userData));

      // Force a complete page reload to the admin portal
      window.location.replace('/manage/shul-settings');

    } catch (err) {
      setError(`Failed to login as user: ${err.message}`);
    }
  };

  // Registration management functions
  const fetchRegistrations = async () => {
    try {
      setRegistrationsLoading(true);
      const data = await api.get(`/master-admin/registrations/?status=${registrationFilter}`);
      setRegistrations(data);
      setError('');
    } catch (err) {
      setError(`Failed to load registrations: ${err.message}`);
    } finally {
      setRegistrationsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 1) {
      fetchRegistrations();
    } else if (activeTab === 2) {
      fetchAnalytics();
    }
  }, [registrationFilter, activeTab, analyticsDays, excludeBots, selectedShulFilter, selectedTrafficSource, selectedDeviceType]);

  const fetchAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      let url = `/master-admin/analytics/?days=${analyticsDays}&exclude_bots=${excludeBots}`;

      if (selectedShulFilter) {
        url += `&shul_id=${selectedShulFilter}`;
      }
      if (selectedTrafficSource) {
        url += `&traffic_source=${selectedTrafficSource}`;
      }
      if (selectedDeviceType) {
        url += `&device_type=${selectedDeviceType}`;
      }

      const data = await api.get(url);
      setAnalytics(data);
      setError('');
    } catch (err) {
      setError(`Failed to load analytics: ${err.message}`);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleClearFilters = () => {
    setAnalyticsDays(30);
    setExcludeBots(true);
    setSelectedShulFilter('');
    setSelectedTrafficSource('');
    setSelectedDeviceType('');
  };

  const handleExportCSV = () => {
    if (!analytics || !analytics.recent_activity) return;

    const headers = ['Time', 'Path', 'Shul', 'Source', 'Referrer', 'Device', 'Browser', 'OS', 'IP', 'Location', 'Speed'];
    const rows = analytics.recent_activity.map(view => [
      new Date(view.timestamp).toLocaleString(),
      view.path,
      view.shul__name || '',
      view.traffic_source || '',
      view.referer_domain || '',
      view.device_type || '',
      view.browser || '',
      view.operating_system || '',
      view.ip_address || '',
      view.city && view.country ? `${view.city}, ${view.country}` : '',
      view.response_time_ms ? `${view.response_time_ms}ms` : ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const handleApproveClick = (registration) => {
    setSelectedRegistration(registration);
    setApproveDialogOpen(true);
  };

  const handleApproveConfirm = async () => {
    if (!selectedRegistration) return;

    try {
      await api.post(`/master-admin/registrations/${selectedRegistration.id}/approve/`);

      // Refresh registrations list
      await fetchRegistrations();

      setSuccessMessage(`Successfully approved "${selectedRegistration.organization_name}" and sent invitation email.`);
      setError('');

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError(`Failed to approve registration: ${err.message}`);
    } finally {
      setApproveDialogOpen(false);
      setSelectedRegistration(null);
    }
  };

  const handleRejectClick = (registration) => {
    setSelectedRegistration(registration);
    setRejectDialogOpen(true);
    setRejectionReason('');
  };

  const handleRejectConfirm = async () => {
    if (!selectedRegistration) return;

    try {
      await api.post(`/master-admin/registrations/${selectedRegistration.id}/reject/`, {
        rejection_reason: rejectionReason,
        send_email: true
      });

      // Refresh registrations list
      await fetchRegistrations();

      setSuccessMessage(`Successfully rejected "${selectedRegistration.organization_name}".`);
      setError('');

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError(`Failed to reject registration: ${err.message}`);
    } finally {
      setRejectDialogOpen(false);
      setSelectedRegistration(null);
      setRejectionReason('');
    }
  };

  const handleDeleteRegistration = async (registration) => {
    if (!window.confirm(`Delete registration request from ${registration.organization_name}?`)) return;

    try {
      await api.delete(`/master-admin/registrations/${registration.id}/delete/`);

      // Remove from list
      setRegistrations(registrations.filter(r => r.id !== registration.id));

      setSuccessMessage('Registration request deleted successfully.');
      setError('');

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError(`Failed to delete registration: ${err.message}`);
    }
  };

  const getDisplayStatus = (lastDisplayAccess) => {
    if (!lastDisplayAccess) {
      return { label: 'Never Used', color: 'default', icon: '⚫' };
    }

    const now = new Date();
    const lastAccess = new Date(lastDisplayAccess);
    const minutesAgo = (now - lastAccess) / 1000 / 60;

    if (minutesAgo <= 5) {
      return { label: 'Online', color: 'success', icon: '🟢' };
    } else if (minutesAgo <= 1440) { // 24 hours
      return { label: 'Recent', color: 'warning', icon: '🟡' };
    } else {
      return { label: 'Inactive', color: 'error', icon: '🔴' };
    }
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return 'Never';
    const date = new Date(dateTime);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <Container maxWidth="xl" className="py-8" sx={{ backgroundColor: 'white', minHeight: '100vh', paddingTop: 4, paddingBottom: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" className="py-8" sx={{ backgroundColor: 'white', minHeight: '100vh', paddingTop: 4, paddingBottom: 4 }}>
      <Box className="mb-6">
        <Typography variant="h4" component="h1" gutterBottom>
          Master Admin Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage all Shuls, registration requests, and global memorial boxes
        </Typography>
        <Box className="mt-4" sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleEditGlobalMemorial}
            size="large"
          >
            Edit Global Memorial Boxes
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleRefresh}
            size="large"
            startIcon={<RefreshIcon />}
          >
            Refresh Data
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" className="mb-4">
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" className="mb-4">
          {successMessage}
        </Alert>
      )}

      <Box className="mb-4">
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Active Shuls" />
          <Tab label="Pending Registrations" />
          <Tab label="Analytics" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Card>
        <CardContent>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Shul Name</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell>Admin Email</TableCell>
                  <TableCell>Display Status</TableCell>
                  <TableCell>Last Display Access</TableCell>
                  <TableCell>Last Admin Login</TableCell>
                  <TableCell>Account Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shuls.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      No Shul found
                    </TableCell>
                  </TableRow>
                ) : (
                  shuls.map((shul) => {
                    const displayStatus = getDisplayStatus(shul.last_display_access);
                    return (
                      <TableRow key={shul.id}>
                        <TableCell>{shul.id}</TableCell>
                        <TableCell>
                          <strong>{shul.name}</strong>
                        </TableCell>
                        <TableCell>
                          <code>{shul.slug}</code>
                        </TableCell>
                        <TableCell>{shul.admin_email}</TableCell>
                        <TableCell>
                          <Chip
                            label={`${displayStatus.icon} ${displayStatus.label}`}
                            color={displayStatus.color}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDateTime(shul.last_display_access)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDateTime(shul.admin_last_login)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={shul.is_active ? 'Active' : 'Inactive'}
                            color={shul.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(shul.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={() => handleLoginAsUser(shul)}
                              title="Login as this user"
                            >
                              Login As
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleViewDisplay(shul.slug)}
                            >
                              View Display
                            </Button>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleDeleteClick(shul)}
                              title="Delete Shul and User Account"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      )}

      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Box className="mb-4" sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Registration Requests
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={registrationFilter === 'pending' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setRegistrationFilter('pending')}
                >
                  Pending
                </Button>
                <Button
                  variant={registrationFilter === 'approved' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setRegistrationFilter('approved')}
                >
                  Approved
                </Button>
                <Button
                  variant={registrationFilter === 'rejected' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setRegistrationFilter('rejected')}
                >
                  Rejected
                </Button>
                <Button
                  variant={registrationFilter === 'all' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setRegistrationFilter('all')}
                >
                  All
                </Button>
              </Box>
            </Box>

            {registrationsLoading ? (
              <Typography>Loading...</Typography>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Submitted</TableCell>
                      <TableCell>Organization</TableCell>
                      <TableCell>Contact Name</TableCell>
                      <TableCell>Rabbi</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Purpose</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {registrations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          No registration requests found
                        </TableCell>
                      </TableRow>
                    ) : (
                      registrations.map((registration) => (
                        <TableRow key={registration.id}>
                          <TableCell>
                            {new Date(registration.submitted_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <strong>{registration.organization_name}</strong>
                          </TableCell>
                          <TableCell>{registration.contact_name}</TableCell>
                          <TableCell>{registration.rabbi || '—'}</TableCell>
                          <TableCell>{registration.email}</TableCell>
                          <TableCell>{registration.phone || '—'}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                              {registration.street_address && (
                                <>
                                  {registration.street_address}<br />
                                </>
                              )}
                              {registration.city}, {registration.state} {registration.zip_code}<br />
                              {registration.country}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {registration.purpose}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={registration.status}
                              color={
                                registration.status === 'approved' ? 'success' :
                                registration.status === 'rejected' ? 'error' :
                                'warning'
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              {registration.status === 'pending' && (
                                <>
                                  <Button
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    startIcon={<CheckCircleIcon />}
                                    onClick={() => handleApproveClick(registration)}
                                  >
                                    Approve
                                  </Button>
                                  <Button
                                    variant="contained"
                                    color="error"
                                    size="small"
                                    startIcon={<CancelIcon />}
                                    onClick={() => handleRejectClick(registration)}
                                  >
                                    Reject
                                  </Button>
                                </>
                              )}
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleDeleteRegistration(registration)}
                                title="Delete registration"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 2 && (
        <Box>
          {/* Analytics Filters */}
          <Card className="mb-4">
            <CardContent>
              <Typography variant="h6" gutterBottom>Filters & Options</Typography>

              {/* Time Range Filters */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Time Range:</Typography>
                <Button
                  variant={analyticsDays === 7 ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setAnalyticsDays(7)}
                >
                  Last 7 Days
                </Button>
                <Button
                  variant={analyticsDays === 30 ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setAnalyticsDays(30)}
                >
                  Last 30 Days
                </Button>
                <Button
                  variant={analyticsDays === 90 ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setAnalyticsDays(90)}
                >
                  Last 90 Days
                </Button>
                <Button
                  variant={excludeBots ? 'contained' : 'outlined'}
                  size="small"
                  color={excludeBots ? 'success' : 'inherit'}
                  onClick={() => setExcludeBots(!excludeBots)}
                >
                  {excludeBots ? 'Excluding Bots' : 'Including Bots'}
                </Button>
              </Box>

              {/* Advanced Filters */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                {/* Shul Filter */}
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Filter by Shul</InputLabel>
                  <Select
                    value={selectedShulFilter}
                    label="Filter by Shul"
                    onChange={(e) => setSelectedShulFilter(e.target.value)}
                  >
                    <MenuItem value="">All Shuls</MenuItem>
                    {shuls.map((shul) => (
                      <MenuItem key={shul.id} value={shul.id}>{shul.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Traffic Source Filter */}
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Traffic Source</InputLabel>
                  <Select
                    value={selectedTrafficSource}
                    label="Traffic Source"
                    onChange={(e) => setSelectedTrafficSource(e.target.value)}
                  >
                    <MenuItem value="">All Sources</MenuItem>
                    <MenuItem value="search">Search (Google, Bing)</MenuItem>
                    <MenuItem value="social">Social Media</MenuItem>
                    <MenuItem value="direct">Direct (Typed URL)</MenuItem>
                    <MenuItem value="referral">Referral Links</MenuItem>
                  </Select>
                </FormControl>

                {/* Device Type Filter */}
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Device Type</InputLabel>
                  <Select
                    value={selectedDeviceType}
                    label="Device Type"
                    onChange={(e) => setSelectedDeviceType(e.target.value)}
                  >
                    <MenuItem value="">All Devices</MenuItem>
                    <MenuItem value="mobile">Mobile</MenuItem>
                    <MenuItem value="tablet">Tablet</MenuItem>
                    <MenuItem value="desktop">Desktop</MenuItem>
                  </Select>
                </FormControl>

                {/* Clear Filters */}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FilterAltOffIcon />}
                  onClick={handleClearFilters}
                >
                  Clear Filters
                </Button>

                {/* Export CSV */}
                <Button
                  variant="contained"
                  size="small"
                  color="success"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportCSV}
                  disabled={!analytics || !analytics.recent_activity || analytics.recent_activity.length === 0}
                >
                  Export CSV
                </Button>
              </Box>
            </CardContent>
          </Card>

          {analyticsLoading ? (
            <Typography>Loading analytics...</Typography>
          ) : analytics ? (
            <>
              {/* Summary Cards */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2, mb: 4 }}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Total Page Views
                    </Typography>
                    <Typography variant="h4">
                      {analytics.summary.total_views.toLocaleString()}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      {analytics.summary.growth_percent >= 0 ? (
                        <TrendingUpIcon color="success" fontSize="small" sx={{ mr: 0.5 }} />
                      ) : (
                        <TrendingDownIcon color="error" fontSize="small" sx={{ mr: 0.5 }} />
                      )}
                      <Typography variant="caption" color={analytics.summary.growth_percent >= 0 ? 'success.main' : 'error.main'}>
                        {analytics.summary.growth_percent >= 0 ? '+' : ''}{analytics.summary.growth_percent}% vs previous period
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Unique Visitors
                    </Typography>
                    <Typography variant="h4">
                      {analytics.summary.unique_sessions.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {analytics.summary.unique_ips.toLocaleString()} unique IPs
                    </Typography>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <SpeedIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography color="text.secondary">
                        Avg Load Time
                      </Typography>
                    </Box>
                    <Typography variant="h4">
                      {analytics.summary.avg_response_time ? `${analytics.summary.avg_response_time}ms` : '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Page response speed
                    </Typography>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Most Active Shul
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                      {analytics.summary.most_active_shul || 'None'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Highest traffic this period
                    </Typography>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Peak Traffic Hour
                    </Typography>
                    <Typography variant="h4">
                      {analytics.summary.peak_hour !== null ? `${analytics.summary.peak_hour}:00` : '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Most views ({Intl.DateTimeFormat('en-US', { hour: 'numeric', hour12: true }).format(new Date().setHours(analytics.summary.peak_hour || 0))})
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              {/* Daily Views Trend Chart */}
              {analytics.daily_views && analytics.daily_views.length > 0 && (
                <Card className="mb-4">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Page Views Trend
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={analytics.daily_views.map(item => ({
                        date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                        views: item.view_count
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="views" stroke="#3b82f6" strokeWidth={2} name="Page Views" />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Views by Shul - Bar Chart */}
              <Card className="mb-4">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <BarChartIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Views by Shul
                  </Typography>
                  {analytics.views_by_shul && analytics.views_by_shul.length > 0 && (
                    <ResponsiveContainer width="100%" height={300}>
                      <RechartsBarChart data={analytics.views_by_shul.slice(0, 10)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="shul__name" angle={-45} textAnchor="end" height={100} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="view_count" fill="#3b82f6" name="Views" />
                      </RechartsBarChart>
                    </ResponsiveContainer>
                  )}
                  <TableContainer component={Paper} elevation={0}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Shul Name</TableCell>
                          <TableCell align="right">Page Views</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {analytics.views_by_shul.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={2} align="center">
                              No shul-specific views found
                            </TableCell>
                          </TableRow>
                        ) : (
                          analytics.views_by_shul.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>{item.shul__name}</TableCell>
                              <TableCell align="right">
                                <Chip label={item.view_count.toLocaleString()} color="primary" size="small" />
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Popular Pages */}
              <Card className="mb-4">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Most Popular Pages
                  </Typography>
                  <TableContainer component={Paper} elevation={0}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Page Path</TableCell>
                          <TableCell align="right">Views</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {analytics.popular_pages.slice(0, 10).map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <code>{item.path}</code>
                            </TableCell>
                            <TableCell align="right">
                              <Chip label={item.view_count.toLocaleString()} color="primary" size="small" />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Traffic Sources - Pie Chart */}
              {analytics.traffic_sources && analytics.traffic_sources.length > 0 && (
                <Card className="mb-4">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Traffic Sources (How They Found You)
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={analytics.traffic_sources.map(item => ({
                            name: item.traffic_source || 'unknown',
                            value: item.count
                          }))}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={(entry) => `${entry.name}: ${entry.value}`}
                        >
                          {analytics.traffic_sources.map((item, index) => {
                            const colors = {
                              'search': '#10b981',
                              'social': '#8b5cf6',
                              'direct': '#3b82f6',
                              'referral': '#f59e0b'
                            };
                            return <Cell key={index} fill={colors[item.traffic_source] || '#6b7280'} />;
                          })}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                      search = Google/Bing, social = Facebook/Twitter, direct = typed URL, referral = other websites
                    </Typography>
                  </CardContent>
                </Card>
              )}

              {/* Geographic Data */}
              {analytics.top_countries && analytics.top_countries.length > 0 && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 4 }}>
                  {/* Top Countries */}
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        <PublicIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                        Top Countries
                      </Typography>
                      <TableContainer component={Paper} elevation={0}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Country</TableCell>
                              <TableCell align="right">Views</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {analytics.top_countries.map((item, index) => (
                              <TableRow key={index}>
                                <TableCell>{item.country}</TableCell>
                                <TableCell align="right">
                                  <Chip label={item.count.toLocaleString()} color="primary" size="small" />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>

                  {/* Top Cities */}
                  {analytics.top_cities && analytics.top_cities.length > 0 && (
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Top Cities
                        </Typography>
                        <TableContainer component={Paper} elevation={0}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>City</TableCell>
                                <TableCell align="right">Views</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {analytics.top_cities.map((item, index) => (
                                <TableRow key={index}>
                                  <TableCell>
                                    {item.city}, {item.country}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Chip label={item.count.toLocaleString()} color="primary" size="small" />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              )}

              {/* Device & Browser Stats */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2, mb: 4 }}>
                {/* Device Types */}
                {analytics.device_types && analytics.device_types.length > 0 && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Device Types
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {analytics.device_types.map((item, index) => (
                          <Chip
                            key={index}
                            label={`${item.device_type || 'unknown'}: ${item.count}`}
                            color={index === 0 ? 'primary' : 'default'}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Operating Systems */}
                {analytics.os_breakdown && analytics.os_breakdown.length > 0 && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Operating Systems
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {analytics.os_breakdown.map((item, index) => (
                          <Chip
                            key={index}
                            label={`${item.operating_system || 'unknown'}: ${item.count}`}
                            color={index === 0 ? 'primary' : 'default'}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Browser Stats */}
                {analytics.browser_stats && analytics.browser_stats.length > 0 && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Browsers
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {analytics.browser_stats.map((item, index) => (
                          <Chip
                            key={index}
                            label={`${item.browser}: ${item.count}`}
                            color={index === 0 ? 'primary' : 'default'}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Box>

              {/* Recent Activity */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Recent Activity (Last 50 Views)
                  </Typography>
                  <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 600 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Time</TableCell>
                          <TableCell>Path</TableCell>
                          <TableCell>Shul</TableCell>
                          <TableCell>Source</TableCell>
                          <TableCell>Referrer</TableCell>
                          <TableCell>Device</TableCell>
                          <TableCell>Browser</TableCell>
                          <TableCell>OS</TableCell>
                          <TableCell>IP</TableCell>
                          <TableCell>Location</TableCell>
                          <TableCell>Speed</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {analytics.recent_activity.map((view) => (
                          <TableRow key={view.id} sx={{ backgroundColor: view.is_bot ? '#fff3cd' : 'inherit' }}>
                            <TableCell>
                              <Typography variant="caption">
                                {new Date(view.timestamp).toLocaleString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <code style={{ fontSize: '0.7rem' }}>{view.path}</code>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">{view.shul__name || '—'}</Typography>
                            </TableCell>
                            <TableCell>
                              {view.traffic_source && (
                                <Chip
                                  label={view.traffic_source}
                                  size="small"
                                  color={
                                    view.traffic_source === 'search' ? 'success' :
                                    view.traffic_source === 'social' ? 'secondary' :
                                    view.traffic_source === 'direct' ? 'primary' :
                                    'default'
                                  }
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">{view.referer_domain || '—'}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">{view.device_type || '—'}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">{view.browser || '—'}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">{view.operating_system || '—'}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">{view.ip_address}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {view.city && view.country ? `${view.city}, ${view.country}` : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {view.response_time_ms ? `${view.response_time_ms}ms` : '—'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </>
          ) : (
            <Typography>No analytics data available</Typography>
          )}
        </Box>
      )}

      {/* Delete Shul Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete <strong>{shulToDelete?.name}</strong>?
            <br /><br />
            This will permanently delete:
            <ul>
              <li>The shul and all its settings</li>
              <li>The user account for <strong>{shulToDelete?.admin_email}</strong></li>
              <li>All associated data (zmanim, custom times, custom texts)</li>
            </ul>
            <strong>This action cannot be undone.</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve Registration Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
      >
        <DialogTitle>Approve Registration</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to approve <strong>{selectedRegistration?.organization_name}</strong>?
            <br /><br />
            An invitation email will be sent to <strong>{selectedRegistration?.email}</strong> with a link to complete their registration.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleApproveConfirm} color="success" variant="contained">
            Approve & Send Email
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Registration Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
      >
        <DialogTitle>Reject Registration</DialogTitle>
        <DialogContent>
          <DialogContentText className="mb-4">
            Reject registration for <strong>{selectedRegistration?.organization_name}</strong>?
          </DialogContentText>
          <TextField
            label="Rejection Reason (optional)"
            multiline
            rows={4}
            fullWidth
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Provide a reason for rejection (will be sent via email)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleRejectConfirm} color="error" variant="contained">
            Reject & Send Email
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default MasterAdminDashboard;
